// assets/js/utils/household-names.js
/**
 * Household names (task #908) — the SINGLE owner of "who is this invitation
 * addressed to, and how do we greet them?".
 *
 * A quarter of the contacts hosts invite are couples ("Natalie & Ehud Nur",
 * "Alan and Linda Jaffe") and a few percent are families ("The Smith Family",
 * "Lucas y Familia", "Mr. & Mrs. Ben Goldstein") — measured 2026-10-10 over
 * 15.7k real invited contacts. Every surface used to take the first word of
 * the name as the "first name", so those guests were greeted "Hi Natalie",
 * "Hi The" and "Hi Mr.", and a family name was counted as a guest.
 *
 *   householdKind(name)        'single' | 'couple' | 'family' | ''
 *   greetingName(name, own)    what {{first_name}} becomes; `own` = a
 *                              greeting the host typed, used as-is
 *   householdPeople(name)      the people a couple name stands for, with the
 *                              shared surname carried ("Sarah & Michael
 *                              Smith" → Sarah Smith, Michael Smith); [] for a
 *                              family name, [name] for one person
 *   mentionsSeveralPeople(name) a looser test for a soft hint on the form
 *
 * Cautious by design. A name that is not clearly a household stays a single
 * person and keeps the old first-word greeting: "José Ortega y Gasset" and
 * "Sônia Souza e Silva" are one person each, and a company like "Verre &
 * Metal" after a comma is not a couple.
 */

const JOINERS = new Set(['&', '+', 'and', 'y', 'e', 'et', 'und', 'ו', 'و']);
// "y" / "e" also join double surnames (Ortega y Gasset, Souza e Silva).
const WEAK_JOINERS = new Set(['y', 'e']);

const FAMILY_WORDS = new Set([
  'family', 'families', 'famille', 'familia', 'flia', 'família', 'familie', 'fam',
  'משפחת', 'משפחה', 'عائلة', 'آل'
]);

// Kept with the SURNAME: "Dr. Sarah Cohen" → "Dr. Cohen".
const HONORIFICS = new Set([
  'mr', 'mrs', 'ms', 'miss', 'mx', 'dr', 'dra', 'prof', 'rabbi', 'rav', 'rev', 'sir', 'lady',
  "r'", 'reb', 'rebbetzin', 'rabbanit', 'harav', 'הרבנית',
  'sr', 'sra', 'srta', 'herr', 'frau', 'mme', 'mlle', 'madame', 'monsieur',
  'הרב', 'ד"ר', 'מר', "גב'", 'גברת', 'السيد', 'السيدة', 'الدكتور', 'الدكتورة'
]);
// Kept with the NEXT word: "Tia Clycia" stays "Tia Clycia".
const KIN = new Set([
  'aunt', 'auntie', 'uncle', 'grandma', 'grandpa', 'granny', 'nana', 'cousin',
  'tia', 'tio', 'tía', 'tío', 'abuela', 'abuelo', 'prima', 'primo',
  'vovó', 'vovô', 'vó', 'vô', 'avó', 'avô', 'tante', 'onkel', 'oma', 'opa',
  'tatie', 'tonton', 'mamie', 'papi', 'דודה', 'סבתא', 'סבא', 'عمو', 'خالة', 'خالو', 'جدو'
]);
// Not 'דוד': uncle, but far more often David ("רחל ודוד פרלמן", 9 of 9 real
// names of that shape), and kin pulls the surname into the greeting.

const bare = (w) => String(w || '').toLowerCase().replace(/[.,]+$/, '');
const isHonorific = (w) => HONORIFICS.has(bare(w));
const isKin = (w) => KIN.has(bare(w));
const isTitle = (w) => isHonorific(w) || isKin(w);

/** Tidy a name: notes in brackets out, "&" / "+" spaced, one space between words. */
function cleanName(name) {
  return String(name == null ? '' : name)
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\[[^\]]*\]/g, ' ')
    .replace(/\s*&\s*/g, ' & ')
    .replace(/\s*\+\s*/g, ' + ')
    .replace(/\s*,\s*/g, ', ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^[,&+\s]+|[,&+\s]+$/g, '');
}

/*
 * Hebrew writes "and" glued to the next word: "שירה ואורן גלבוע", "סבא וסבתא
 * תור". Measured 2026-10-12: ~400 of 2,215 real Hebrew contact names have
 * that shape, while a free-standing "ו" appears once. A glued ו is also the
 * first letter of surnames (וייס, וולוובסקי, ויצמן, ורנר) and of a few first
 * names (ורד, ויקטוריה), so it only counts as "and" when the name is three
 * or more words, the word does not look like a surname (וו / ויי = W),
 * and no other joiner is written. A two-word "תמר ואיתמר" stays ambiguous
 * with "יובל ורנר" (one person) and only feeds the soft form hint.
 */
const VAV_NAMES = new Set([
  'ורד', 'ורדה', 'ורדית', 'ויקטור', 'ויקטוריה', 'ויקי', 'ויויאן', 'ולנטינה',
  'ולדימיר', 'ולרי', 'ולריה', 'ונסה', 'ויטלי', 'ויטה'
]);
const HEB_GLUED_VAV = /^ו[א-ת][א-ת'"׳״-]+$/;
const looksLikeVavSurname = (w) => /^(וו|ויי)/.test(w) || VAV_NAMES.has(w);

/** Index of the glued ו word that reads as "and", or -1. */
function gluedVavIndex(words, minWords) {
  if (words.length < minWords) return -1;
  if (words.some(w => JOINERS.has(w.toLowerCase()))) return -1;
  for (let i = 1; i < words.length; i++) {
    const w = words[i].replace(/,$/, '');
    if (HEB_GLUED_VAV.test(w) && !looksLikeVavSurname(w)) return i;
  }
  return -1;
}

/**
 * Split into segments at joiners and commas. Each segment: { words, sep }
 * where `sep` is what follows it (',' or the joiner as written, '' at end).
 */
function segmentsOf(clean) {
  const segs = [{ words: [], sep: '' }];
  const toks = clean.split(' ');
  const vav = gluedVavIndex(toks, 3);
  if (vav > 0) toks.splice(vav, 1, 'ו', toks[vav].slice(1));
  toks.forEach((tok) => {
    const cur = segs[segs.length - 1];
    if (JOINERS.has(tok.toLowerCase()) && cur.words.length) {
      cur.sep = tok;
      segs.push({ words: [], sep: '' });
      return;
    }
    if (tok.endsWith(',') && tok.length > 1) {
      cur.words.push(tok.slice(0, -1));
      cur.sep = ',';
      segs.push({ words: [], sep: '' });
      return;
    }
    cur.words.push(tok);
  });
  return segs.filter((s, i) => s.words.length || i === 0);
}

const coreWords = (seg) => seg.words.filter(w => !isTitle(w));

function isFamily(clean) {
  const words = clean.split(' ');
  if (words.some(w => FAMILY_WORDS.has(bare(w)))) return true;
  // "The Smiths", "The Cohen Family" (handled above), "the Levis".
  return words.length >= 2 && bare(words[0]) === 'the';
}

/** Parsed couple/list, or null when the name is not clearly several people. */
function parseCouple(clean) {
  const segs = segmentsOf(clean);
  if (segs.length < 2 || segs.some(s => !s.words.length)) return null;
  const joined = segs.slice(0, -1).map(s => s.sep);
  if (!joined.some(s => s !== ',')) return null; // commas alone: "Smith, John"
  if (segs.length > 8) return null;
  const last = segs[segs.length - 1];
  const head = segs.slice(0, -1);
  // "Mr. & Mrs. Ben Goldstein": the leading segments are titles only.
  // (Both sides titled; "Rabbi and Rivkah Denburg" is a title and a name.)
  const titleCouple = head.every(s => s.words.every(isHonorific)) && isHonorific(last.words[0])
    && coreWords(last).length >= 1;
  if (titleCouple) return { segs, titleCouple: true };
  // A comma list names one word each before the last: "Sarah, Michael and Emma".
  if (joined.includes(',') && head.some(s => coreWords(s).length !== 1)) return null;
  // A title alone stands for a person only in a pair: "Rabbi and Rivkah Denburg".
  // So does kin alone: "Grandma and Grandpa Cohen", "סבא וסבתא תור".
  const titleAlone = (s) => segs.length === 2 && s.words.every(isTitle);
  if (head.some(s => (coreWords(s).length < 1 && !titleAlone(s)) || coreWords(s).length > 2)) return null;
  if ((coreWords(last).length < 1 && !titleAlone(last)) || coreWords(last).length > 3) return null;
  // A weak joiner between two-word sides is a double surname ("José Ortega y
  // Gasset") unless both sides are full names ("Juan Pérez y María López").
  const firstJoiner = joined.find(s => s !== ',');
  if (WEAK_JOINERS.has(String(firstJoiner).toLowerCase())
      && coreWords(segs[0]).length > 1 && coreWords(last).length < 2) return null;
  return { segs, titleCouple: false };
}

function householdKind(name) {
  const clean = cleanName(name);
  if (!clean) return '';
  if (isFamily(clean)) return 'family';
  return parseCouple(clean) ? 'couple' : 'single';
}

/** The greeting part of one person's words: first name, or title + name. */
function personGreeting(words) {
  if (!words.length) return '';
  if (isKin(words[0])) return words.slice(0, 2).join(' ');
  if (isHonorific(words[0])) {
    let i = 0;
    while (i < words.length && isHonorific(words[i])) i++;
    const titles = words.slice(0, i).join(' ');
    const rest = words.slice(i);
    // Four or more names after the title is a Spanish-style double surname:
    // "Dr. Gil Arturo Quijano Vega" is Dr. Quijano Vega.
    const surname = rest.length >= 4 ? rest.slice(-2).join(' ') : rest[rest.length - 1];
    return rest.length ? `${titles} ${surname}` : titles;
  }
  return words[0];
}

const sepText = (sep) => (sep === ',' ? ', ' : sep === 'ו' ? ' ו' : ` ${sep} `);

/**
 * "Ryan and Nicki Gilbert and family", "Lucas y Familia": people named in
 * front of a closing family word. null when the name doesn't end that way.
 */
function familyTail(clean) {
  const w = clean.split(' ');
  for (let j = w.length - 2; j > 0; j--) {
    if (!JOINERS.has(w[j].toLowerCase())) continue;
    const rest = w.slice(j + 1);
    if (!rest.every(x => FAMILY_WORDS.has(bare(x)))) return null;
    return { prefix: w.slice(0, j).join(' '), joiner: w[j], tail: rest.join(' ') };
  }
  return null;
}

/**
 * Israeli guest lists are often sorted by surname: "כהן יעל ודורון" is Yael
 * and Doron Cohen (≈25 of the ~30 real names of this shape). null otherwise.
 */
function surnameFirst(segs) {
  if (segs.length !== 2 || segs[0].sep !== 'ו') return null;
  const [a, b] = [segs[0].words, segs[1].words];
  if (a.length !== 2 || b.length !== 1 || a.some(isTitle)) return null;
  return { surname: a[0], first: a[1], second: b[0] };
}

function greetingName(name, own) {
  const custom = String(own == null ? '' : own).replace(/\s+/g, ' ').trim();
  if (custom) return custom;
  const clean = cleanName(name);
  if (!clean) return '';
  if (isFamily(clean)) {
    const tail = familyTail(clean);
    if (tail) return `${greetingName(tail.prefix)} ${tail.joiner} ${tail.tail}`;
    const words = clean.split(' ');
    return (bare(words[0]) === 'the' && words.length > 1 ? words.slice(1) : words).join(' ');
  }
  const couple = parseCouple(clean);
  if (!couple) return personGreeting(clean.split(' '));
  const { segs } = couple;
  const sf = surnameFirst(segs);
  if (sf) return `${sf.first}${sepText('ו')}${sf.second}`;
  if (couple.titleCouple) {
    const last = segs[segs.length - 1];
    const lastTitles = [];
    for (const w of last.words) { if (isHonorific(w)) lastTitles.push(w); else break; }
    const surname = last.words[last.words.length - 1];
    return segs.slice(0, -1).map(s => s.words.join(' ') + sepText(s.sep)).join('') + `${lastTitles.join(' ')} ${surname}`.trim();
  }
  return segs.map((s, i) => personGreeting(s.words) + (i < segs.length - 1 ? sepText(s.sep) : '')).join('');
}

function householdPeople(name) {
  const clean = cleanName(name);
  if (!clean) return [];
  if (isFamily(clean)) {
    const tail = familyTail(clean);
    return tail ? householdPeople(tail.prefix) : [];
  }
  const couple = parseCouple(clean);
  if (!couple) return [clean];
  const { segs } = couple;
  const sf = surnameFirst(segs);
  if (sf) return [`${sf.surname} ${sf.first}`, `${sf.surname} ${sf.second}`];
  const last = segs[segs.length - 1];
  if (couple.titleCouple) {
    let i = 0;
    while (i < last.words.length && isHonorific(last.words[i])) i++;
    const rest = last.words.slice(i);
    const surname = rest[rest.length - 1];
    const titles = segs.slice(0, -1).map(s => s.words.join(' ')).concat(last.words.slice(0, i).join(' '));
    return titles.map((t, k) => (k === 0 ? `${t} ${rest.join(' ')}` : `${t} ${surname}`).trim());
  }
  // "Sarah & Michael Smith": the surname at the end belongs to everyone; a
  // title alone ("Rabbi and Rivkah Denburg") is that title and the surname.
  const carry = coreWords(last).length >= 2 && segs.slice(0, -1).every(s => coreWords(s).length <= 1)
    ? last.words[last.words.length - 1] : '';
  return segs.map((s, i) => (i < segs.length - 1 && carry ? `${s.words.join(' ')} ${carry}` : s.words.join(' ')));
}

// A looser test, only for a soft "is this for more than one person?" hint:
// any household name, or an unambiguous joiner anywhere ("Gilbert Haugan and
// possibly Dustin Haugan & kids") even when the parse above gives up.
function mentionsSeveralPeople(name) {
  const clean = cleanName(name);
  if (!clean) return false;
  if (householdKind(clean) !== 'single') return true;
  // "תמר ואיתמר": too ambiguous to count as two, fine to ask about. Not
  // "וי…", which is mostly a W surname here (ויצמן, וינר, וינטר).
  const words = clean.split(' ');
  const v = gluedVavIndex(words, 2);
  if (v > 0 && !/^וי/.test(words[v])) return true;
  return clean.split(' ').some((w, i, all) => i > 0 && i < all.length - 1
    && JOINERS.has(w.toLowerCase()) && !WEAK_JOINERS.has(w.toLowerCase()));
}

/*
 * Group Name (task #935). The guest list has two name columns: "Guest
 * name(s)" (the people) and "Group name" (who the invitation is addressed to,
 * "The Smith Family"). Stored on the existing model, no migration:
 *   name      = the first person, or the group name when no one is listed
 *   members   = every person, when there is a group or more than one person
 *   groupName = the group name, when there is one
 * `groupName` is what says the invitation's name is a GROUP and not a person
 * - "Ellina's cousins" reads as one person to householdKind.
 */
const cleanGroupName = (raw) => String(raw == null ? '' : raw).replace(/\s+/g, ' ').trim().slice(0, 120);

/** { name, groupName?, members? } from the two columns. people: [{name, guestType}]. */
function guestListContact(people, groupName) {
  const g = cleanGroupName(groupName);
  const list = (Array.isArray(people) ? people : [])
    .map(p => ({ name: String((p && p.name) || '').replace(/\s+/g, ' ').trim().slice(0, 120), guestType: p && p.guestType === 'child' ? 'child' : 'adult' }))
    .filter(p => p.name).slice(0, 20);
  const out = { name: list.length ? list[0].name : g };
  if (g) out.groupName = g;
  if (list.length && (g || list.length > 1)) out.members = list;
  return out;
}

/**
 * The two columns for a stored contact: { people, groupName }. Contacts saved
 * before the columns existed read naturally: "The Smith Family" + members →
 * group + people; "Esther Stolbach" + "Shahar Weil" → both people.
 */
function guestListColumns(contact) {
  const c = contact || {};
  const name = String(c.name || '').replace(/\s+/g, ' ').trim();
  const members = (Array.isArray(c.members) ? c.members : []).filter(m => m && String(m.name || '').trim())
    .map(m => ({ name: String(m.name).trim(), guestType: m.guestType === 'child' ? 'child' : 'adult' }));
  let groupName = cleanGroupName(c.groupName);
  const kind = !groupName && name ? householdKind(name) : '';
  if (kind === 'family') groupName = name;
  const key = (v) => cleanName(v).toLowerCase();
  let people = members;
  if (kind && kind !== 'single' && members.length) {
    // A couple's or a family's name over a list of people - "Natalie & Ehud
    // Nur" + Natalie Nur, Ehud Nur is what #908's "count them" writes. The
    // name addresses the invitation; it is not a person. Read the way the
    // guest's link reads it (guest-allowlist normalizeHouseholdRoster): the
    // people the name stands for that the list lacks ("Sarah" listed counts
    // as "Sarah Smith"), then the list. Read as one person, a table that
    // round-trips the columns turned Natalie Nur into "Natalie & Ehud Nur".
    groupName = name;
    const listed = members.filter(m => key(m.name) !== key(name));
    const first = (v) => key(v).split(' ')[0];
    const oneWord = (v) => key(v).split(' ').length === 1;
    const isListed = (p) => listed.some(m => key(m.name) === key(p) || (first(m.name) === first(p) && (oneWord(m.name) || oneWord(p))));
    people = householdPeople(name).filter(p => !isListed(p))
      .map(p => ({ name: p.slice(0, 120), guestType: 'adult' })).concat(listed);
  } else if (name && key(name) !== key(groupName)) {
    // Listed already (in full, or as "Evelyn" under "Evelyn Cohen"): that
    // entry is them, under their full name. Otherwise they come first.
    const at = members.findIndex(m => key(m.name) === key(name) || repeatsMainGuest(m, name));
    people = at >= 0 ? members.map((m, i) => (i === at ? { ...m, name } : m))
      : [{ name, guestType: 'adult' }].concat(members);
  }
  return { people, groupName };
}

/**
 * What the RSVP form welcomes a household as ("Welcome, Smith Family!"): the
 * host's own greeting, the group, else the name. Group-first, like the
 * envelope - a message names the people instead (invitationGreeting).
 */
function invitationHouseholdGreeting(contact) {
  const c = contact || {};
  const own = String(c.greeting == null ? '' : c.greeting).replace(/\s+/g, ' ').trim();
  if (own) return own;
  const g = cleanGroupName(c.groupName);
  // "The Smith Family" greets as "Smith Family"; a group name that is not a
  // household by its words ("Ellina's cousins") is used as written.
  if (g) return householdKind(g) === 'single' ? g : greetingName(g);
  return greetingName(c.name);
}

// More people than this and a message names the group instead: "Hi Esti,
// Rivka, Sara, Moshe, Yosef & Chaim" reads as a roll call, and a WhatsApp
// name parameter is cut at a length a big family passes.
const MESSAGE_NAMES_MAX = 4;

/**
 * "Natasha & Reuben", "Esti, Rivka, & Sara" (the serial comma, Esther task
 * #956); Hebrew / Arabic names take a glued ו / و and no serial comma
 * ("שרה, מיכאל ואמה"). "&" reads the same in every Latin-script language.
 */
function joinNames(list) {
  const names = (list || []).filter(Boolean);
  if (names.length < 2) return names[0] || '';
  const last = names[names.length - 1];
  const glued = /[א-ת]/.test(last) ? ' ו' : /[ء-ي]/.test(last) ? ' و' : '';
  const and = glued || (names.length > 2 ? ', & ' : ' & ');
  return names.slice(0, -1).join(', ') + and + last;
}

// The people a MESSAGE names (Esther, task #935 2026-10-14): the envelope says
// "The Stolbach Family", the message "Hi Esti, Rivka, & Sara" - and with no
// Group name, "Sarah; Michael" is "Hi Sarah & Michael", not "Hi Sarah" (round
// 3). Read through guestListColumns, so a contact saved before the columns
// existed lists its main guest too. One entry with no group keeps its name as
// typed ("Sarah & Michael Smith"); a group nobody is listed on names nobody.
function messagePeople(c) {
  const cols = guestListColumns(c);
  const entries = cols.people;
  if (!entries.length || (!cols.groupName && entries.length < 2)) return null;
  const people = [];
  entries.forEach((m) => {
    const each = householdPeople(m.name);
    (each.length ? each : [m.name]).forEach((name) => people.push({ name, guestType: m.guestType }));
  });
  // Over four, a group is greeted by its name; with no group there is no
  // shorter name to use, so everyone is named.
  return people.length <= MESSAGE_NAMES_MAX || !cols.groupName ? people : null;
}

/** What a message calls the invitation ({{first_name}}): the host's own greeting, the people's first names, the group, else the name. */
function invitationGreeting(contact) {
  const c = contact || {};
  const own = String(c.greeting == null ? '' : c.greeting).replace(/\s+/g, ' ').trim();
  if (own) return own;
  const people = messagePeople(c);
  if (people) return joinNames(people.map(p => greetingName(p.name)));
  return invitationHouseholdGreeting(c);
}

/** {{name}} in a message: the people listed (2+, or under a group) ("Natasha Feldman & Reuben Lewis"), else who the invitation is addressed to. */
function invitationMessageName(contact) {
  const c = contact || {};
  const people = messagePeople(c);
  if (people) return joinNames(people.map(p => cleanName(p.name)));
  return invitationAddressee(c);
}

/** Who the envelope / card header is addressed to: the group, else everyone listed, else the name. */
function invitationAddressee(contact) {
  const c = contact || {};
  const g = cleanGroupName(c.groupName);
  if (g) return g;
  // "Sarah; Michael" with no Group name is addressed to both (task #935
  // round 3). A family name over its people ("The Smith Family" + members,
  // saved before the columns) still addresses as that name.
  if (Array.isArray(c.members) && c.members.length) {
    const cols = guestListColumns(c);
    if (!cols.groupName && cols.people.length >= 2) return joinNames(cols.people.map(p => cleanName(p.name)));
    if (cols.groupName) return cols.groupName;
  }
  return String(c.name || '').replace(/\s+/g, ' ').trim();
}

// The surname(s) an invitation's name gives the people listed under it by
// first name only, so "Sarah" can find her family's invitation as "Sarah
// Smith" (task #935): "The Smith Family" / "Smith Family" / "משפחת כהן" →
// Smith / כהן; "The Levis" → Levis or Levi; "Sarah & Michael Smith" and
// "Mr. & Mrs. Smith" → Smith; "Ryan and Nicki Gilbert and family" → Gilbert;
// one person "Evelyn Rose Cohen" → "Rose Cohen". [] when the name gives none:
// a first name alone, "Lucas y Familia", "Sarah & Michael", or a couple with
// two different surnames - whose would it be.
function householdSurnames(name) {
  const clean = cleanName(name);
  if (!clean) return [];
  if (isFamily(clean)) {
    const tail = familyTail(clean);
    if (tail) return householdSurnames(tail.prefix);
    const words = clean.split(' ');
    const rest = words.filter((w, i) => !(i === 0 && bare(w) === 'the') && !FAMILY_WORDS.has(bare(w))
      && !isTitle(w) && !JOINERS.has(w.toLowerCase()));
    if (!rest.length) return [];
    const out = [rest.join(' ')];
    // "The Smiths" names the family by its plural.
    if (!words.some(w => FAMILY_WORDS.has(bare(w)))) {
      const last = rest[rest.length - 1];
      const head = rest.slice(0, -1);
      if (/es$/i.test(last) && last.length > 4) out.push(head.concat(last.slice(0, -2)).join(' '));
      if (/s$/i.test(last) && last.length > 3) out.push(head.concat(last.slice(0, -1)).join(' '));
    }
    return out;
  }
  if (!parseCouple(clean)) {
    const core = clean.split(' ').filter(w => !isTitle(w));
    return core.length >= 2 ? [core.slice(1).join(' ')] : [];
  }
  const lasts = householdPeople(clean).map((p) => {
    const core = p.split(' ').filter(w => !isTitle(w));
    return core.length ? core[core.length - 1] : '';
  });
  return lasts.length && lasts[0] && lasts.every(l => l.toLowerCase() === lasts[0].toLowerCase()) ? [lasts[0]] : [];
}

/*
 * Which typed names find an invitation (task #935) - ONE rule for the
 * server's lookup (guest-allowlist memberLookupNames) and the hosts' previews
 * (import wizard, contacts table). Three copies of it had already drifted: a
 * preview promised "Noa cousins" for a group the lookup gives no surname.
 */

/** Surnames a first-name-only person takes: the group's when it is named as a household, then the invitation's own name's. */
// A Group name of one word is the family's surname: "Stolbach" + Esti is
// found as "Esti Stolbach", as "The Stolbach Family" is (task #1004, Esther's
// #935 rule: "a single shared surname like Stolbach"). A word that names a
// circle rather than a family ("Friends", "Neighbors") is not.
const NOT_A_SURNAME = new Set([
  'friends', 'neighbors', 'neighbours', 'cousins', 'colleagues', 'coworkers', 'co-workers',
  'team', 'office', 'work', 'guests', 'kids', 'children', 'parents', 'grandparents',
  'חברים', 'שכנים', 'עבודה', 'أصدقاء', 'الأصدقاء', 'amigos', 'amis', 'freunde'
]);
function oneWordGroupSurname(groupName) {
  const words = cleanName(groupName).split(' ').filter(Boolean);
  if (words.length !== 1) return [];
  const w = words[0];
  if (isTitle(w) || FAMILY_WORDS.has(bare(w)) || NOT_A_SURNAME.has(bare(w)) || !/^\p{L}[\p{L}'’-]+$/u.test(w)) return [];
  return [w];
}

function invitationSurnames(contact) {
  const c = contact || {};
  const g = cleanGroupName(c.groupName);
  const fromGroup = !g ? [] : householdKind(g) !== 'single' ? householdSurnames(g) : oneWordGroupSurname(g);
  const ownName = String(c.name || '').trim();
  const own = ownName && cleanName(ownName).toLowerCase() !== cleanName(g).toLowerCase() ? householdSurnames(ownName) : [];
  const seen = new Set();
  return fromGroup.concat(own).filter((s) => { const k = s.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
}

/** The people an invitation names, one per person: a couple written as one entry ("Sarah & Michael Smith") is two. */
function invitationPeople(contact) {
  const c = contact || {};
  const entries = Array.isArray(c.members) && c.members.length ? c.members
    : (c.name && cleanName(c.name).toLowerCase() !== cleanName(c.groupName).toLowerCase() ? [{ name: c.name, guestType: 'adult' }] : []);
  const out = [];
  entries.forEach((m) => {
    if (!m || !String(m.name || '').trim()) return;
    const each = householdPeople(m.name);
    (each.length ? each : [m.name]).forEach((name) => out.push({ name, guestType: m.guestType === 'child' ? 'child' : 'adult' }));
  });
  return out;
}

/**
 * What a guest can type to find the invitation: each person's full name, a
 * person listed by first name only with each invitation surname, and - for a
 * group nobody is named on - the group name ("Gold Family" finds "The Gold
 * Family"). A bare first name never finds one, so it is never listed.
 * (Email and phone find it too.)
 */
function invitationLookupNames(contact) {
  const c = contact || {};
  const out = [];
  const seen = new Set();
  const add = (v) => {
    const s = String(v || '').replace(/\s+/g, ' ').trim();
    const k = s.toLowerCase();
    if (s && !seen.has(k)) { seen.add(k); out.push(s); }
  };
  const g = cleanGroupName(c.groupName);
  const people = invitationPeople(c);
  // The group name finds only a group nobody is named on; where people are
  // listed each types their own name (the industry norm, Donny 2026-10-14).
  if (g && !people.length) {
    const w = g.split(' ');
    add(/^the$/i.test(w[0]) && w.length > 2 ? w.slice(1).join(' ') : g);
  }
  const surnames = invitationSurnames(c);
  people.forEach((p) => {
    const words = cleanName(p.name).split(' ').filter(Boolean);
    if (words.length >= 2) add(words.join(' '));
    else if (words.length === 1) surnames.forEach((s) => add(`${words[0]} ${s}`));
  });
  return out;
}

/**
 * The different-last-names tip: the party's people do not share one surname,
 * or someone listed by first name only gets none - then only their own full
 * name, phone or email finds the invitation for them.
 */
function invitationNeedsSurnameTip(contact) {
  const people = invitationPeople(contact);
  if (people.length < 2) return false;
  const surnames = invitationSurnames(contact);
  const lasts = new Set();
  for (const p of people) {
    const words = cleanName(p.name).split(' ').filter(Boolean);
    if (words.length >= 2) lasts.add(words[words.length - 1].toLowerCase());
    else if (surnames.length) lasts.add(surnames[0].split(' ').pop().toLowerCase());
    else return true;
  }
  return lasts.size > 1;
}

// Does a Party-members entry name the invitation's own main guest again?
// Hosts list the whole party in that column, main guest included, and often
// by first name only: "Evelyn Cohen" + "Evelyn; Sam; Mandy" with Max guests 3
// is three people. Compared on full names alone, "Evelyn" looked like a
// second person and 84 of 105 rows in one real upload (2026-10-13) carried
// the main guest twice. A full name equal to one of the people the main name
// stands for, or a bare first name equal to that person's first name, is
// them. A child entry never is - a "Jr." sharing a parent's first name is
// written that way.
function repeatsMainGuest(member, mainName) {
  const m = member && typeof member === 'object' ? member : { name: member };
  if (m.guestType === 'child') return false;
  const key = (v) => cleanName(v).toLowerCase().split(' ').map(bare).filter(Boolean);
  const mw = key(m.name);
  if (!mw.length) return false;
  return householdPeople(mainName).some((person) => {
    const pw = key(person);
    if (mw.join(' ') === pw.join(' ')) return true;
    const given = pw.filter(w => !isTitle(w));
    return mw.length === 1 && given.length >= 2 && mw[0] === given[0];
  });
}

/*
 * Seat names (task #965) - ONE rule for every place a reply's party is listed
 * person by person: the seating tab, server auto-assign, check-in at the door
 * and the per-guest meal list. Seats are matched by name across them, so a
 * second copy of the rule would drift and leave a seat nobody sits in.
 */

// The family surname a first-name-only person in the party takes: the
// invitation's Group name when it names a household ("Stolbach Family" →
// Stolbach, round 2), else the reply's own name when that is a family name.
// A plural family name gives its surname as written: "The Smiths" seats Ann
// Smiths (Esther, round 3: the host knows who is meant). Not the singular -
// "The Williams" / "The Adams" are already singular, and Ann William would be
// wrong. Search finds either form (partySearchNames). Never the main guest's
// own surname: "David Cohen" + "Sarah" may not be Sarah Cohen, and a wrong
// surname on the door list is worse than none.
function partySurnames(mainName, groupName) {
  const g = cleanGroupName(groupName);
  if (g && householdKind(g) !== 'single') return householdSurnames(g);
  return householdKind(mainName) === 'family' ? householdSurnames(mainName) : [];
}

function partySurname(mainName, groupName) {
  return partySurnames(mainName, groupName)[0] || '';
}

// The names a party member is found by in a search: as typed, and with each
// form of the family's surname, so "Ann Smith" and "Ann Smiths" both find Ann
// on The Smiths' invitation.
function partySearchNames(name, mainName, groupName) {
  const out = [name];
  partySurnames(mainName, groupName).forEach((s) => {
    const full = withSurname(name, s);
    if (full !== name) out.push(full);
  });
  return out;
}

// "Esti" + Stolbach → "Esti Stolbach". Only a one-word name, and only as
// typed: a couple, a full name or a name carrying a note stays as it is.
function withSurname(name, surname) {
  const raw = String(name == null ? '' : name).replace(/\s+/g, ' ').trim();
  if (!surname || !raw || cleanName(raw) !== raw) return name;
  const words = raw.split(' ').filter(w => !isTitle(w));
  if (words.length !== 1 || bare(words[0]) === bare(surname)) return name;
  return `${raw} ${surname}`;
}

// The main guest's seats. `others` are the party's other entries ({ name,
// guestType } or names), `total` the party size.
//  - "Tsivia & Hillel" with Hillel listed again is Tsivia: four people, Hillel
//    once (round 1).
//  - "Irwin & Susan" for 2 with nobody named is two seats, Irwin and Susan,
//    not "Irwin & Susan" and "Irwin & Susan +1" (round 2) - when the party
//    has an unnamed seat for each of them. "Gershon & Livia" for 1 stays one
//    seat: the head count is the guest's answer, the name is not.
// Unchanged when everybody is listed again (the first seat is then somebody
// the names do not tell us).
function mainSeatNames(mainName, others, total, groupName) {
  const surname = partySurname(mainName, groupName);
  const people = householdPeople(mainName);
  if (people.length < 2) return [withSurname(mainName, surname)];
  // An entry may itself be a couple ("Danielle and Natan" under "Danielle &
  // Natan Schwartz & family"): it is the people it names.
  const list = [];
  (Array.isArray(others) ? others : []).forEach((o) => {
    const entry = o && typeof o === 'object' ? o : { name: o };
    if (!cleanName(entry.name)) return;
    const each = householdPeople(entry.name);
    (each.length > 1 ? each : [entry.name]).forEach(name => list.push({ ...entry, name }));
  });
  const left = people.filter(person => !list.some(o => repeatsMainGuest(o, person)));
  if (!left.length) return [mainName];
  const spare = (Number(total) || 0) - 1 - list.length;
  if (left.length > 1 && spare >= left.length - 1) return left.map(p => withSurname(p, surname));
  return [left.length === people.length ? mainName : joinNames(left)];
}

// Anyone else in the party, by the same rule.
function memberSeatName(name, mainName, groupName) {
  return withSurname(name, partySurname(mainName, groupName));
}

// A reply's named party entries other than the main guest, as { name,
// guestType } - what mainSeatNames counts. individualNames entries are
// { position, name } objects or, on old replies, plain names.
function partyOthers(individualNames, mainName) {
  return (Array.isArray(individualNames) ? individualNames : [])
    .map(e => (e && typeof e === 'object' ? e : { name: String(e || '') }))
    .filter(e => e.position !== 1 && String(e.name || '').trim() && e.name !== mainName);
}

const HouseholdNames = {
  householdKind, greetingName, householdPeople, householdSurnames, mentionsSeveralPeople, cleanName, repeatsMainGuest,
  mainSeatNames, memberSeatName, partyOthers, partySearchNames,
  guestListContact, guestListColumns, invitationGreeting, invitationHouseholdGreeting, invitationMessageName,
  invitationAddressee, cleanGroupName, joinNames,
  invitationSurnames, invitationPeople, invitationLookupNames, invitationNeedsSurnameTip
};

// Dual-environment, like guest-counts.js: the browser gets the global, the
// server requires this same file.
if (typeof window !== 'undefined') window.HouseholdNames = HouseholdNames;
if (typeof module !== 'undefined' && module.exports) module.exports = HouseholdNames;
